import React, { useState } from "react";
import { Outlet, NavLink, useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../lib/auth";
import { useSubscription } from "../lib/subscription";
import {
  LayoutDashboard,
  PlusSquare,
  ListChecks,
  FileBarChart2,
  History,
  Settings,
  LogOut,
  Menu,
  X,
  Receipt,
  Plus,
  Users,
  CreditCard,
  TicketPercent,
  AlertTriangle,
} from "lucide-react";

const baseNavItems = [
  { to: "/", label: "لوحة التحكم", icon: LayoutDashboard, end: true, testid: "nav-dashboard" },
  { to: "/add", label: "إضافة فاتورة", icon: PlusSquare, testid: "nav-add" },
  { to: "/invoices", label: "سجل الفواتير", icon: ListChecks, testid: "nav-invoices" },
  { to: "/reports", label: "التقارير", icon: FileBarChart2, testid: "nav-reports" },
  { to: "/reports/history", label: "سجل الشهور", icon: History, testid: "nav-history" },
  { to: "/subscription", label: "الاشتراك", icon: CreditCard, testid: "nav-subscription" },
  { to: "/settings", label: "الإعدادات", icon: Settings, testid: "nav-settings" },
];

const adminNavItems = [
  { to: "/admin/users", label: "إدارة المستخدمين", icon: Users, testid: "nav-admin" },
  { to: "/admin/coupons", label: "الخطط والكوبونات", icon: TicketPercent, testid: "nav-coupons" },
];

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const { sub } = useSubscription();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const navItems = isAdmin ? [...baseNavItems, ...adminNavItems] : baseNavItems;

  // Renewal reminder — trial or paid subscription about to expire
  const showExpiryBanner =
    !isAdmin && sub && sub.active && sub.days_remaining != null && sub.days_remaining <= 5 &&
    location.pathname !== "/subscription";

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  // Show FAB on dashboard and invoices pages (not on add/review to avoid duplication)
  const showFab = ["/", "/invoices", "/reports", "/settings"].includes(location.pathname);

  return (
    <div className="min-h-screen bg-[#F5F4EF] flex" dir="rtl">
      {/* Mobile top bar (hidden on lg+) */}
      <header className="lg:hidden fixed top-0 inset-x-0 z-30 bg-white border-b border-[#1A1C19]/10 px-4 py-3 flex items-center justify-between">
        <button
          data-testid="mobile-menu-btn"
          className="p-2 -ms-2"
          onClick={() => setOpen(true)}
          aria-label="القائمة"
        >
          <Menu className="w-6 h-6 text-[#1A1C19]" strokeWidth={1.5} />
        </button>
        <div className="flex items-center gap-2 font-readex font-semibold text-[#1F3A33]">
          <Receipt className="w-5 h-5" strokeWidth={1.5} />
          <span>فواتيري</span>
        </div>
        <div className="w-10" />
      </header>

      {/* Sidebar — desktop (flex column, takes its own column width — no overlap with content) */}
      <aside className="hidden lg:flex w-72 flex-shrink-0 bg-white border-s border-[#1A1C19]/10 sticky top-0 h-screen flex-col z-20">
        <SidebarContent user={user} onLogout={handleLogout} onClose={null} navItems={navItems} />
      </aside>

      {/* Sidebar — mobile drawer */}
      {open && (
        <div className="lg:hidden fixed inset-0 z-40">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setOpen(false)}
          />
          <aside className="absolute top-0 right-0 h-full w-72 bg-white flex flex-col fade-up">
            <SidebarContent user={user} onLogout={handleLogout} onClose={() => setOpen(false)} navItems={navItems} />
          </aside>
        </div>
      )}

      {/* Main content — flex-1 takes the remaining width */}
      <main className="flex-1 min-w-0 min-h-screen pt-14 lg:pt-0">
        {showExpiryBanner && (
          <div className="bg-[#FDF6E9] border-b border-[#E8B96E]/50 px-4 py-2.5 flex items-center justify-center gap-2 text-sm text-[#7A5B16] font-readex" data-testid="expiry-banner">
            <AlertTriangle className="w-4 h-4 shrink-0" />
            <span>
              {sub.is_trial ? "فترتك التجريبية" : "اشتراكك"} ينتهي خلال {sub.days_remaining} {sub.days_remaining === 1 ? "يوم" : "أيام"} —
            </span>
            <Link to="/subscription" className="underline font-medium" data-testid="expiry-banner-link">اشترك الآن</Link>
          </div>
        )}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 md:px-10 py-6 sm:py-10 pb-24 lg:pb-10">
          <Outlet />
        </div>
      </main>

      {/* Mobile FAB — quick add invoice */}
      {showFab && (
        <button
          onClick={() => navigate("/add")}
          data-testid="mobile-fab-add"
          aria-label="إضافة فاتورة"
          className="lg:hidden fixed bottom-5 start-5 z-30 w-14 h-14 rounded-full bg-[#1F3A33] text-white elevate-pulse hover:bg-[#172A25] active:scale-95 transition-all flex items-center justify-center"
        >
          <Plus className="w-7 h-7" strokeWidth={1.75} />
        </button>
      )}
    </div>
  );
}

function SidebarContent({ user, onLogout, onClose, navItems }) {
  return (
    <>
      <div className="px-6 py-6 border-b border-[#1A1C19]/10 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-md bg-[#1F3A33] text-white flex items-center justify-center">
            <Receipt className="w-5 h-5" strokeWidth={1.5} />
          </div>
          <div>
            <div className="font-readex font-semibold text-lg text-[#1A1C19] leading-tight">فواتيري</div>
            <div className="text-xs text-[#8A8D86]">إدارة الفواتير</div>
          </div>
        </div>
        {onClose && (
          <button onClick={onClose} className="p-1" aria-label="إغلاق">
            <X className="w-5 h-5 text-[#5C605A]" strokeWidth={1.5} />
          </button>
        )}
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navItems.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              data-testid={item.testid}
              onClick={onClose || undefined}
              className={({ isActive }) =>
                `relative flex items-center gap-3 px-4 py-3 rounded-md text-[15px] font-readex transition-all ${
                  isActive
                    ? "bg-[#1F3A33] text-white shadow-[0_2px_8px_rgba(31,58,51,0.25)]"
                    : "text-[#1A1C19] hover:bg-[#F0EFEB] hover:translate-x-[-2px]"
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <Icon className="w-5 h-5" strokeWidth={1.5} />
                  <span>{item.label}</span>
                  {isActive && (
                    <span className="absolute inset-y-2 start-0 w-[3px] rounded-e-full bg-[#6A8A76]" aria-hidden="true" />
                  )}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>

      <div className="px-4 py-4 border-t border-[#1A1C19]/10">
        <div className="px-2 py-2 mb-2">
          <div className="text-xs text-[#8A8D86] uppercase tracking-wider mb-1">الحساب</div>
          <div className="font-readex font-medium text-[#1A1C19] truncate" data-testid="user-name">
            {user?.name || user?.email}
          </div>
          <div className="text-xs text-[#5C605A] truncate">{user?.email}</div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={onLogout}
          className="w-full flex items-center gap-3 px-4 py-3 rounded-md text-[#C84C31] hover:bg-[#C84C31]/10 transition-colors font-readex"
        >
          <LogOut className="w-5 h-5" strokeWidth={1.5} />
          تسجيل الخروج
        </button>
      </div>
    </>
  );
}
